/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { numberToCurrency } from "@/utils/money";
import truncateText from "@/utils/truncateQuery";
import { ExtendedColumnDef } from "components/Datatable";
import { Checkbox } from "components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "components/ui/popover";
import { useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  UseFormReturn,
} from "react-hook-form";
import { IDivergence } from "src/@types/divergence";
import DatePickerCell from "./components";
import { ChevronRight, Mail } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";

const useColumns = (
  methods: UseFormReturn<FieldValues, any, undefined>,
  errors: { [field: string]: string } | undefined
) => {
  const { control, setValue } = methods;
  const [rowId] = useState();

  const columns: ExtendedColumnDef<IDivergence>[] = [
    {
      accessorKey: "operator",
      header: "Operador",
      cell: ({ row }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        return (
          <div className={`flex items-center gap-2 ${hasEmail ? "opacity-50" : ""}`}>
            {hasEmail && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2B0C1A]/10 hover:bg-[#2B0C1A]/20 transition-colors cursor-help">
                      <Mail className="h-3.5 w-3.5 text-[#2B0C1A]" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="bg-[#2B0C1A] text-white border-none z-[9999]"
                    sideOffset={5}
                  >
                    <p className="font-medium">Email em andamento</p>
                    <p className="text-xs text-white/80">Aguardando resposta ou revisão</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span>{values.operator}</span>
          </div>
        );
      },
      className: "disabled-column operator",
    },
    {
      accessorKey: "description",
      header: "Descrição da Divergência",
      cell: ({ row }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        return <div className={hasEmail ? "opacity-60" : ""}>{values.name}</div>;
      },
      className: "disabled-column description",
    },
    {
      accessorKey: "value",
      header: "Valor",
      cell: ({ row }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        return <div className={hasEmail ? "opacity-60" : ""}>{numberToCurrency(values.amount)}</div>;
      },
      className: "disabled-column value",
    },
    {
      id: "conferred",
      header: () => (
        <div className="flex w-full align-center justify-start">
          {" "}
          Comentários <span className="text-[#d0312d] ml-[3px]">*</span>
        </div>
      ),
      cell: ({ row }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        // Só registra valores no form se não tiver email pendente
        if (!hasEmail) {
          setValue(`divergence_${row.original.id}.version`, row.original.version);
          setValue(`divergence_${row.original.id}.id`, row.original.id);
        }

        const errorMsg = errors
          ? errors[`analyst_${row.original.id}`]
          : undefined;

        return (
          <div
            className={`flex align-center justify-center w-full input-container min-w-[120px] ${hasEmail ? "opacity-60" : ""}`}
          >
            <FormProvider {...methods}>
              <form>
                <div className="w-full h-full flex items-center justify-center">
                  <Controller
                    name={`divergence_${values.id}.analyst`}
                    control={control}
                    defaultValue={values?.analyst_comments ?? ''}
                    render={({ field }) => {
                      return (
                        <input
                          type="text"
                          onChange={(value) => {
                            if (!hasEmail) {
                              setValue(
                                `divergence_${row.original.id}.analyst`,
                                value.target.value
                              );
                            }
                          }}
                          value={field.value}
                          placeholder={hasEmail ? "Email já enviado" : "Escreva sua observação"}
                          className={`bg-white ${errorMsg ? "has-error" : ""} ${hasEmail ? "cursor-not-allowed bg-gray-100" : ""}`}
                          disabled={hasEmail}
                          readOnly={hasEmail}
                        />
                      );
                    }}
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        );
      },
      className: "form-column",
    },
    {
      accessorKey: "store_justification",
      header: "Justificativa da Loja",
      cell: ({ row }) => {
        const values = row.original;
        const justification = values?.store_justification?.toString() ?? '';
        const hasContent = justification.length > 0;

        return (
          <div className="text-black">
            <Popover modal={true}>
              <PopoverTrigger className="w-full group flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded transition-colors">
                <span className="flex-1 text-sm truncate">
                  {hasContent
                    ? truncateText(justification, 20)
                    : <span className="text-gray-400 italic">Sem justificativa</span>
                  }
                </span>
                {hasContent && (
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#4B303C] transition-colors" />
                )}
              </PopoverTrigger>
              <PopoverContent
                className="w-[400px] p-0 shadow-lg"
                side="right"
                align="start"
              >
                <div className="p-4 max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
                      {justification}
                    </p>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        );
      },
    },
    {
      id: "has_dependency",
      header: () => (
        <div className="flex w-full align-center justify-start">
          {" "}
          Dependência
        </div>
      ),
      cell: ({ row }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        // Só registra valores no form se não tiver email pendente
        if (!hasEmail) {
          setValue(
            `divergence_${row.original.id}.has_dependency`,
            row.original.has_dependency
          );
        }

        return (
          <div className={hasEmail ? "opacity-60" : ""}>
            {rowId === values.id ? (
              <div className="w-full flex items-center justify-center">
                <svg
                  className="animate-spin h-5 w-5 text-[#2B0C1A]"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            ) : (
              <FormProvider {...methods}>
                <form>
                  <div className="w-full flex items-center justify-center checkbox">
                    <Controller
                      name={`divergence_${values.id}.has_dependency`}
                      control={control}
                      defaultValue={values?.has_dependency ?? false}
                      render={({ field }) => (
                        <Checkbox
                          id={`divergence_${values.id}.has_dependency`}
                          checked={field.value ?? false}
                          onCheckedChange={(checked) => {
                            if (!hasEmail) {
                              setValue(
                                `divergence_${values.id}.has_dependency`,
                                checked
                              );
                            }
                          }}
                          disabled={hasEmail}
                          className={`data-[state=checked]:bg-[#F5CB2B] data-[state=checked]:text-[#2B0C1A] ${hasEmail ? "cursor-not-allowed" : ""}`}
                        />
                      )}
                    />
                  </div>
                </form>
              </FormProvider>
            )}
          </div>
        );
      },
      className: "form-column",
    },
    {
      id: "dependency_date",
      header: () => (
        <div className="flex items-center justify-start w-max">
          Data Dependência
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        const values = row.original;
        const hasEmail = values.has_email_solicitation;

        // Só registra valores no form se não tiver email pendente
        if (!hasEmail) {
          setValue(
            `divergence_${values.id}.dependency_date`,
            values.dependency_date
          );
        }

        return (
          <div className={`w-full flex items-center justify-center p-0 ${hasEmail ? "opacity-60 pointer-events-none" : ""}`}>
            <DatePickerCell
              date={values.dependency_date}
              control={control}
              isLoading={rowId === values.id}
              row={row}
              onDateChange={(date: Date | undefined) => {
                if (!hasEmail) {
                  setValue(
                    `divergence_${row.original.id}.dependency_date`,
                    date!.toISOString()
                  );
                }
              }}
            />
          </div>
        );
      },
      className: "min-w-[200px] items-center justify-center !p-0",
    },
  ];

  return columns;
};

export default useColumns;
